import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useContext, useEffect } from 'react';
import {
  $createLocalizableTextNode,
  $isLocalizableTextNode,
  LocalizableTextNode,
} from 'pages/authorized/page-designer/lexical/nodes/Localizable/LocalizableTextNode';
import {
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_HIGH,
  CONTROLLED_TEXT_INSERTION_COMMAND,
  COPY_COMMAND,
  CUT_COMMAND,
  DROP_COMMAND,
  INSERT_PARAGRAPH_COMMAND,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  KEY_DOWN_COMMAND,
  NodeKey,
  PASTE_COMMAND,
  SELECTION_INSERT_CLIPBOARD_NODES_COMMAND,
} from 'lexical';
import { useSettings } from 'pages/authorized/page-designer/lexical/context/SettingsContext.tsx';
import { PageDesignerContext } from '../../DesignerNewPage.tsx';
import {
  $createProtectedParagraphNode,
  $isProtectedParagraphNode,
} from '../nodes/Localizable/ProtectedParagraphNode.ts';
import { $isImageNode } from '../nodes/ImageNode.tsx';
import {
  $createLocalizationNode,
  $isLocalizationNode,
} from '../nodes/Localizable/LocalizationNode.tsx';
import { useTranslation } from 'react-i18next';
import { getCurrentSupportedLanguage } from 'application/localization/localization.ts';
import { Language } from 'services/api/api-client.types.ts';
import { DefaultLanguageContext } from '../../SinglePage.tsx';
import { setIsCopying } from 'pages/authorized/page-designer/lexical/plugins/CopyingState.ts';
import { $isLayoutContainerNode } from 'pages/authorized/page-designer/lexical/nodes/LayoutContainerNode.ts';
import { $isTabContainerNode } from 'pages/authorized/page-designer/lexical/nodes/TabContainerNode.ts';
import { $isListItemNode, $isListNode, ListItemNode } from '@lexical/list';
import { $isHeadingNode } from '@lexical/rich-text';
import { SupportedNode } from 'pages/authorized/page-designer/lexical/plugins/CustomTranslateIconPlugin.tsx';
import {
  $createLocalizationLinkNode,
  $isLocalizationLinkNode,
} from 'pages/authorized/page-designer/lexical/nodes/Localizable/LocalizationLinkNode.ts';
import { $isTabItemNode } from 'pages/authorized/page-designer/lexical/nodes/TabItemNode.ts';

function CustomTextNodePlugin() {
  const [editor] = useLexicalComposerContext();

  const {
    settings: { isPageDesigner },
  } = useSettings();

  const i18n = useTranslation();
  const currentOuterLanguage = getCurrentSupportedLanguage(i18n.i18n);

  const pageDesignerContext = useContext(PageDesignerContext);
  const defaultLanguageContext = useContext(DefaultLanguageContext);

  const currentLanguage = isPageDesigner
    ? pageDesignerContext?.pageEditorLanguage
    : ((currentOuterLanguage.at(0)?.toUpperCase() +
        currentOuterLanguage.slice(1)) as Language);

  const defaultLanguage = isPageDesigner
    ? pageDesignerContext?.defaultLanguage
    : defaultLanguageContext?.defaultLanguage;

  const addLocalizationNode = (node: SupportedNode) => {
    const keys: NodeKey[] = [];

    node.getChildren().forEach((childNode) => {
      if (childNode.getType() === 'localization-node') {
        keys.push(childNode.getKey());
      }
    });

    while (keys.length > 1) {
      const key = keys.pop();

      if (key) {
        const node = $getNodeByKey(key);
        if (node) {
          node.remove();
        }
      }
    }

    if (!keys.length) {
      const localizationNode = $createLocalizationNode();
      node.append(localizationNode);
    }
  };

  // language switcher logic
  useEffect(() => {
    editor.update(
      () => {
        if (!currentLanguage) {
          return;
        }

        const root = $getRoot();
        const first = root.getFirstChild();

        if (first) {
          const domNode = editor.getElementByKey(first.getKey());

          if (domNode && isPageDesigner) {
            requestAnimationFrame(() => {
              const toolbarHeight = 60;
              const rect = domNode.getBoundingClientRect();
              const absoluteY = rect.top + window.scrollY;

              window.scrollTo({
                top: absoluteY - toolbarHeight,
                behavior: 'smooth',
              });
            });
          }
        }

        root.getAllTextNodes().forEach((node) => {
          if ($isLocalizableTextNode(node) && defaultLanguage) {
            node.setPageDesignerLanguage(currentLanguage);

            const translation =
              node.getTranslation(currentLanguage) ||
              node.getTranslation(defaultLanguage);

            if (translation && node.getTextContent() !== translation) {
              node.setTextContent(translation);
            }
          }
        });
      },
      { tag: 'history-merge' },
    );
  }, [currentLanguage, editor, isPageDesigner]);

  useEffect(() => {
    editor.update(() => {
      const root = $getRoot();
      const children = root.getChildren();

      children.forEach((node) => {
        if (
          $isProtectedParagraphNode(node) ||
          $isHeadingNode(node) ||
          $isTabContainerNode(node)
        ) {
          addLocalizationNode(node);
        } else if ($isListNode(node)) {
          const children = node.getChildren();
          for (const child of children) {
            if ($isListItemNode(child)) {
              addLocalizationNode(child);
            }
          }
        }
      });
    });
  }, [editor, isPageDesigner, currentLanguage]);

  // translation change logic
  useEffect(() => {
    const unregister = editor.registerUpdateListener(
      ({ editorState, dirtyLeaves }) => {
        if (!currentLanguage || !defaultLanguage) {
          return;
        }

        const nodeKeysToUpdate = editorState.read(() => {
          return Array.from(dirtyLeaves)
            .map((nodeKey) => $getNodeByKey(nodeKey))
            .filter($isLocalizableTextNode)
            .map((node) => node.getKey());
        });

        if (nodeKeysToUpdate.length === 0) {
          return;
        }

        editor.update(
          () => {
            for (const key of nodeKeysToUpdate) {
              const node = $getNodeByKey(key);
              if (!$isLocalizableTextNode(node)) continue;

              if (!node.__originalLanguage) {
                node.setOriginalLanguage(defaultLanguage);
              }

              const currentText = node.getTextContent();

              if (
                node.getTranslation(currentLanguage) === currentText ||
                (!node.getTranslation(currentLanguage) &&
                  node.getTranslation(defaultLanguage) === currentText) ||
                pageDesignerContext?.isVersionsModeActive
              ) {
                continue;
              }

              node.setTranslations(currentLanguage, currentText);
              node.removeLanguageManuallyChanged(currentLanguage);
              if (currentText) {
                node.addLanguageManuallyChanged(currentLanguage);
              }
              node.setPageDesignerLanguage(currentLanguage);

              if (currentLanguage === node.__originalLanguage) {
                node.resetLanguagesTranslatedByAI();
              }
            }
          },
          { tag: 'history-merge' },
        );
      },
    );

    return () => {
      unregister();
    };
  }, [
    editor,
    currentLanguage,
    defaultLanguage,
    pageDesignerContext?.isVersionsModeActive,
  ]);

  // creating and removing node's rules
  useEffect(() => {
    const unregisterColumnsInsertCommand = editor.registerUpdateListener(
      ({ editorState }) => {
        editorState.read(() => {
          const root = $getRoot();
          const last = root.getLastChild();

          if ($isLayoutContainerNode(last) || $isTabContainerNode(last)) {
            editor.update(() => {
              const newParagraph = $createProtectedParagraphNode();
              root.append(newParagraph);
            });
          }
        });
      },
    );

    const unregisterInsertLineBreakCommand = editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event: KeyboardEvent) => {
        if (event.key === 'Enter' && event.shiftKey) {
          event.preventDefault();
          return true;
        }
        return false;
      },
      COMMAND_PRIORITY_HIGH,
    );

    const unregisterCopyCommand = editor.registerCommand(
      COPY_COMMAND,
      (event: Event | null) => {
        setIsCopying(true);
        setTimeout(() => setIsCopying(false), 0);
        return false;
      },
      COMMAND_PRIORITY_HIGH,
    );

    const unregisterTabLinkTextInsert = editor.registerCommand(
      CONTROLLED_TEXT_INSERTION_COMMAND,
      (text: string) => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          return false;
        }

        const nodes = selection.getNodes();
        if (nodes.length > 1) {
          return false;
        }

        const anchorNode = selection.anchor.getNode();
        const parentNode = anchorNode.getParent();

        if ($isElementNode(anchorNode)) {
          const children = anchorNode.getChildren();

          for (const child of children) {
            if (
              $isLocalizableTextNode(child) &&
              child.getTextContent() === ''
            ) {
              child.setTextContent(text);
              child.select(text.length, text.length);
              return true;
            }
          }
        }

        if (
          $isLocalizationLinkNode(parentNode) &&
          $isLocalizableTextNode(anchorNode) &&
          parentNode.isInsideTab()
        ) {
          const anchorOffset = selection.anchor.offset;
          const focusOffset = selection.focus.offset;

          const start = Math.min(anchorOffset, focusOffset);
          const end = Math.max(anchorOffset, focusOffset);

          const writableNode = anchorNode.getWritable();
          writableNode.spliceText(start, end - start, text);

          const insertedLength = text.length;
          const newOffset = start + insertedLength;
          writableNode.select(newOffset, newOffset);

          return true;
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH,
    );

    const unregisterTabLinkTextPaste = editor.registerCommand(
      SELECTION_INSERT_CLIPBOARD_NODES_COMMAND,
      (clipboardNodes) => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          return false;
        }

        const nodes = selection.getNodes();
        if (nodes.length > 1) {
          return false;
        }

        const anchorNode = selection.anchor.getNode();

        if ($isLocalizableTextNode(anchorNode)) {
          let pastedText = '';

          clipboardNodes.nodes.forEach((node) => {
            if ($isTextNode(node)) {
              pastedText += node.getTextContent();
            } else if ($isElementNode(node)) {
              node.getChildren().forEach((child) => {
                if ($isTextNode(child)) {
                  pastedText += child.getTextContent();
                }
              });
            }
          });

          if (pastedText === '') {
            return false;
          }

          const anchorOffset = selection.anchor.offset;
          const focusOffset = selection.focus.offset;

          const start = Math.min(anchorOffset, focusOffset);
          const end = Math.max(anchorOffset, focusOffset);

          const writableNode = anchorNode.getWritable();
          writableNode.spliceText(start, end - start, pastedText);

          const newOffset = start + pastedText.length;
          writableNode.select(newOffset, newOffset);

          return true;
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH,
    );

    const unregisterWrapTextInLink = editor.registerNodeTransform(
      LocalizableTextNode,
      (textNode) => {
        const text = textNode.getTextContent();

        if (!textNode.isInsideTab() || !text) {
          return;
        }

        editor.update(() => {
          const newLinkNode = $createLocalizationLinkNode();
          const newTextNode = $createLocalizableTextNode(text, null, null);

          newLinkNode.append(newTextNode);
          textNode.replace(newLinkNode);
        });
      },
    );

    const unregisterDeleteCommandDefaultLanguage = editor.registerCommand(
      KEY_DELETE_COMMAND,
      (event) => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return false;

        const anchorNode = selection.anchor.getNode();
        const parentNode = anchorNode.getParent();

        if (
          ($isLocalizableTextNode(anchorNode) &&
            $isLocalizationLinkNode(parentNode) &&
            parentNode.isInsideTab() &&
            selection.anchor.offset === anchorNode.getTextContentSize()) ||
          ($isProtectedParagraphNode(anchorNode) && $isTabItemNode(parentNode))
        ) {
          event.preventDefault();
          return true;
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH,
    );

    if (defaultLanguage === currentLanguage) {
      return () => {
        unregisterInsertLineBreakCommand();
        unregisterColumnsInsertCommand();
        unregisterCopyCommand();
        unregisterTabLinkTextInsert();
        unregisterTabLinkTextPaste();
        unregisterWrapTextInLink();
        unregisterDeleteCommandDefaultLanguage();
      };
    }

    const unregisterPasteCommand = editor.registerCommand(
      PASTE_COMMAND,
      (event: ClipboardEvent) => {
        const text = event.clipboardData?.getData('text/plain') ?? '';

        const selection = $getSelection();
        const nodes = selection?.getNodes();

        if (!$isRangeSelection(selection)) {
          return false;
        }

        if (nodes && nodes.length > 1) {
          return true;
        }

        const nodeToPaste = selection.anchor.getNode();
        const parent = nodeToPaste.getParent();
        const isInList = parent instanceof ListItemNode;

        if (isInList && $isLocalizableTextNode(nodeToPaste)) {
          editor.update(() => {
            event.preventDefault();

            const anchorOffset = selection.anchor.offset;
            const focusOffset = selection.focus.offset;

            const start = Math.min(anchorOffset, focusOffset);
            const end = Math.max(anchorOffset, focusOffset);

            const writableNode = nodeToPaste.getWritable();
            writableNode.spliceText(start, end - start, text);
          });
        }

        return isInList;
      },
      COMMAND_PRIORITY_HIGH,
    );

    const unregisterCreateTextNodeListener = editor.registerNodeTransform(
      LocalizableTextNode,
      (node) => {
        if (
          $isLocalizableTextNode(node) &&
          defaultLanguage &&
          currentLanguage &&
          !pageDesignerContext?.isVersionsModeActive
        ) {
          node.remove();
        }
      },
    );

    const unregisterInsertParagraphCommand = editor.registerCommand(
      INSERT_PARAGRAPH_COMMAND,
      (_payload) => true,
      COMMAND_PRIORITY_HIGH,
    );

    const unregisterClipboardInsertCommand = editor.registerCommand(
      SELECTION_INSERT_CLIPBOARD_NODES_COMMAND,
      (clipboardNodes) => {
        const selection = $getSelection();
        const nodes = selection?.getNodes();

        if (!$isRangeSelection(selection)) {
          return false;
        }

        if (nodes && nodes.length > 1) {
          return true;
        }

        const anchorNode = selection.anchor.getNode();

        if ($isLocalizableTextNode(anchorNode)) {
          let pastedText = '';

          clipboardNodes.nodes.forEach((node) => {
            if ($isTextNode(node)) {
              pastedText += node.getTextContent();
            } else if ($isElementNode(node)) {
              node.getChildren().forEach((child) => {
                if ($isTextNode(child)) {
                  pastedText += child.getTextContent();
                }
              });
            }
          });

          if (pastedText !== '') {
            const anchorOffset = selection.anchor.offset;
            const focusOffset = selection.focus.offset;

            const start = Math.min(anchorOffset, focusOffset);
            const end = Math.max(anchorOffset, focusOffset);

            const writableNode = anchorNode.getWritable();
            writableNode.spliceText(start, end - start, pastedText);

            return true;
          }
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH,
    );

    const unregisterBackspaceCommand = editor.registerCommand(
      KEY_BACKSPACE_COMMAND,
      (event) => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          return false;
        }

        if (!selection.isCollapsed()) {
          const nodes = selection.getNodes();

          if (nodes.length > 1) {
            event.preventDefault();
            return true;
          }

          return false;
        }

        const anchorNode = selection.anchor.getNode();
        const anchorOffset = selection.anchor.offset;

        if (anchorOffset === 0) {
          const parent = anchorNode.getParent();
          const previousNode = anchorNode.getPreviousSibling();

          if (
            parent &&
            (previousNode === null || $isLocalizationNode(previousNode))
          ) {
            event.preventDefault();
            return true;
          }
        }

        if (
          $isLocalizableTextNode(anchorNode) &&
          anchorNode.getTextContent() === ''
        ) {
          const parent = anchorNode.getParent();

          if (parent && $isProtectedParagraphNode(parent)) {
            event.preventDefault();
            return true;
          }
        }

        if ($isProtectedParagraphNode(anchorNode)) {
          event.preventDefault();
          return true;
        }

        if ($isImageNode(anchorNode)) {
          event.preventDefault();
          return true;
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH,
    );

    const unregisterDeleteCommand = editor.registerCommand(
      KEY_DELETE_COMMAND,
      (event) => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return false;

        if (!selection.isCollapsed()) {
          const nodes = selection.getNodes();

          if (nodes.length > 1) {
            event.preventDefault();
            return true;
          }
          return false;
        }

        const anchorNode = selection.anchor.getNode();

        if (
          $isLocalizableTextNode(anchorNode) &&
          anchorNode.getTextContent() === ''
        ) {
          const parent = anchorNode.getParent();

          if (parent && $isProtectedParagraphNode(parent)) {
            event.preventDefault();
            return true;
          }
        }

        if ($isProtectedParagraphNode(anchorNode)) {
          event.preventDefault();
          return true;
        }

        if (
          $isLocalizableTextNode(anchorNode) &&
          selection.anchor.offset === anchorNode.getTextContentSize()
        ) {
          event.preventDefault();
          return true;
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH,
    );

    const unregisterInsertText = editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event) => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          const nodes = selection.getNodes();
          if (nodes.length > 1 && !event.ctrlKey && !event.metaKey) {
            event.preventDefault();
            return true;
          }
        }
        return false;
      },
      COMMAND_PRIORITY_HIGH,
    );

    const unregisterCutCommand = editor.registerCommand(
      CUT_COMMAND,
      (event) => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection) || selection.isCollapsed()) {
          return false;
        }

        const nodes = selection.getNodes();
        if (nodes.length > 1 && event) {
          event.preventDefault();
          return true;
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH,
    );

    const unregisterDropCommand = editor.registerCommand(
      DROP_COMMAND,
      (event) => {
        event.preventDefault();
        return true;
      },
      COMMAND_PRIORITY_HIGH,
    );

    return () => {
      unregisterInsertParagraphCommand();
      unregisterInsertLineBreakCommand();
      unregisterClipboardInsertCommand();
      unregisterBackspaceCommand();
      unregisterDeleteCommand();
      unregisterInsertText();
      unregisterCutCommand();
      unregisterDropCommand();
      unregisterCopyCommand();
      unregisterColumnsInsertCommand();
      unregisterPasteCommand();
      unregisterCreateTextNodeListener();
      unregisterTabLinkTextInsert();
      unregisterTabLinkTextPaste();
      unregisterWrapTextInLink();
      unregisterDeleteCommandDefaultLanguage();
    };
  }, [editor, currentLanguage, defaultLanguage]);

  useEffect(() => {
    const rootElement = editor.getRootElement();
    if (!rootElement) return;

    const handleDragStart = (e: DragEvent) => {
      e.preventDefault();
      return false;
    };

    rootElement.addEventListener('dragstart', handleDragStart);

    if (
      defaultLanguage !== currentLanguage ||
      pageDesignerContext?.isVersionsModeActive
    ) {
      rootElement.classList.add('hideRemoveButtons');
    }

    return () => {
      rootElement.removeEventListener('dragstart', handleDragStart);
      rootElement.classList.remove('hideRemoveButtons');
    };
  }, [
    editor,
    currentLanguage,
    defaultLanguage,
    pageDesignerContext?.isVersionsModeActive,
  ]);

  return null;
}

export default CustomTextNodePlugin;
